import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import useLocalStorage from "../utils/localstorage";
import { useToast } from "./useToast";
import { useApi } from "../utils/useAPI";

export default function useAuthentication() {
  const [token, setToken] = useLocalStorage("token", {} as { access: string; expired: number; type: string });
  const [phoneNumber, setPhoneNumber] = useLocalStorage("phone-number", "");
  const [customerName, setCustomerName] = useLocalStorage("customer-name", "");
  const [loading, setLoading] = useState(false);
  const { presentToast } = useToast();
  const { apiFetch } = useApi();

  const check = useCallback(
    async (phone: string, callback: (register: boolean) => void) => {
      setLoading(true);
      return await apiFetch(process.env.REACT_APP_API_URL + "/api/pos-order/outlet-service/phone/check", {
        body: JSON.stringify({ phone }),
        headers: {
          Authorization: `${token.type} ${token.access}`,
          ContentType: "application/json",
        },
        method: "POST",
      })
        .then((data) => {
          if (data?.status === "success") callback(data.result.register);
          else if (data?.status === "fail") {
            presentToast({ message: data.messages, color: "danger" });
          }
        })
        .catch((error) => {
          presentToast({ message: error, color: "danger" });
        })
        .finally(() => setLoading(false));
    },
    [token.access, token.type]
  );

  const request = useCallback(
    async (name: string, phone: string) => {
      return await apiFetch(process.env.REACT_APP_API_URL + "/api/pos-order/outlet-service/pin/request", {
        body: JSON.stringify({ name, phone, request_type: process.env.REACT_APP_REQUEST_TYPE }),
        headers: { Authorization: `${token.type} ${token.access}`, ContentType: "application/json" },
        method: "POST",
      })
        .then((data) => {
          return data;
        })
        .catch((error) => {
          presentToast({ message: error, color: "danger" });
        });
    },
    [token.access, token.type]
  );

  const handleRequestToken = useCallback(async () => {
    return await fetch(process.env.REACT_APP_API_URL + "/oauth/token", {
      body: JSON.stringify({
        client_id: process.env.REACT_APP_CLIENT_ID,
        client_secret: process.env.REACT_APP_CLIENT_SECRET,
        grant_type: "client_credentials",
        scope: "pos-order",
      }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    })
      .then((response) => response.json())
      .then((data) => {
        setToken({ access: data.access_token, expired: data.expires_in, type: data.token_type });
      })
      .catch((error) => {
        return error;
      });
  }, [setToken]);

  const resetPhoneNumber = () => setPhoneNumber("");
  const resetCustomerName = () => setCustomerName("");

  useEffect(() => {
    if (dayjs.unix(token?.expired as number).diff(dayjs()) < 1 || token?.expired === undefined) {
      handleRequestToken();
    }
  }, [handleRequestToken, token?.expired]);

  return { handle: { check, request, loading }, phoneNumber, resetPhoneNumber, token, resetCustomerName, customerName, setCustomerName, setPhoneNumber };
}
